/**
 * Dashboard Filters API
 *
 * API client functions for fetching filter options (job categories, users)
 */

import { apiClient } from '@/lib/api';

export interface JobCategory {
  id: number;
  name: string;
  job_audit_form_id: number | null;
  created_time: string;
  client_id: number;
}

export interface JobCategoryResponse {
  message: string;
  status: boolean;
  values: JobCategory[];
}

export interface User {
  id: number;
  username: string;
  cognito_username: string;
  first_name: string;
  second_name: string;
  preferred_name: string | null;
  job_title: string | null;
  full_name: string;
  name: string;
  update_username: string | null;
}

export interface UserResponse {
  message: string;
  count: number;
  values: User[];
}

export interface Priority {
  name: string;
  id: number;
}

export interface PriorityResponse {
  message: string;
  status: boolean;
  values: Priority[];
}

/**
 * Fetch job categories for filter dropdown
 *
 * @param search - Optional search query
 * @param limit - Number of results to return
 * @returns Job categories data
 */
export async function getJobCategories(search?: string, limit = 100): Promise<JobCategoryResponse> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('limit', String(limit));
  params.append('skip', '0');

  const queryString = params.toString();
  const response = await apiClient.get<JobCategoryResponse>(
    `/job-category/list${queryString ? `?${queryString}` : ''}`,
  );

  // Deduplicate categories by ID (backend JOIN may return duplicates)
  const uniqueCategories = Array.from(
    new Map(response.values.map((category) => [category.id, category])).values(),
  );

  return {
    ...response,
    values: uniqueCategories,
  };
}

/**
 * Fetch users for assignee filter
 * Returns active, verified users only
 *
 * @returns User list data
 */
export async function getUsers(): Promise<UserResponse> {
  return apiClient.get<UserResponse>('/user/simple-list');
}

/**
 * Fetch priorities for priority filter
 *
 * @returns Priority list data
 */
export async function getPriorities(): Promise<PriorityResponse> {
  return apiClient.get<PriorityResponse>('/job/priority/list');
}
